/**
 * Shared API and formatting helpers for patient detail views.
 * Use these to fetch enrollments/appointments and format data for reuse across patient details.
 */

/**
 * Fetches patient enrollments for a given patient.
 * @param {Function} axios - Configured axios instance
 * @param {string|number} patientId - Patient id
 * @returns {Promise<Array|null>} - enrollment array or null on error/empty
 */
export const fetchPatientEnrollments = async (axios, patientId) => {
    try {
        const res = await axios.get(`enrollment/patient/${patientId}`);
        if (Array.isArray(res.data.data)) {
            return res.data.data;
        }
        console.error("Can't fetch patient enrollments");
        return null;
    } catch (err) {
        console.log(err);
        return null;
    }
};

/**
 * Fetches patient appointments for a given patient.
 * @param {Function} axios - Configured axios instance
 * @param {string|number} patientId - Patient id
 * @returns {Promise<Array|null>} - raw appointment array or null on error/empty
 */
export const fetchPatientAppointments = async (axios, patientId) => {
    try {
        const res = await axios.get(`appointment/patient?patientId=${patientId}`);
        if (Array.isArray(res.data.data)) {
            return res.data.data;
        }
        console.error("Can't fetch patient appointments");
        return null;
    } catch (err) {
        console.log(err);
        return null;
    }
};

/**
 * Derives questionnaire IDs and fetches full questionnaire data from enrollment data.
 * @param {Function} axios - Configured axios instance
 * @param {Array} enrollmentData - Result of fetchPatientEnrollments
 * @returns {Promise<Array>} - questionnaire objects with totalPossibleScore
 */
export const getQuestionnaires = async (axios, enrollmentData) => {
    if (!Array.isArray(enrollmentData)) return [];
    const currentEnrollments = enrollmentData.filter((item) => !item.enrollment.dischargeDate);
    const questionnaires = [];
    currentEnrollments.forEach((enrollment) => {
        if (enrollment.questionnaires && enrollment.questionnaires.length !== 0) {
            enrollment.questionnaires.forEach((q) => {
                questionnaires.push(q.questionnaireId);
            });
        }
    });
    const qIds = Array.from(new Set(questionnaires));
    const qPromises = qIds.map(async (q) => {
        try {
            const response = await axios.get(`/questionnaire/qId/${q}`);
            if (response.data.data) {
                const question = response.data.data;
                let scoreCount = 0;
                (response.data.data.questions || []).forEach((qu) => {
                    let maxScore = 0;
                    if (qu.answerType === "Multi Select") {
                        (qu.possibleAnswers || []).forEach((a) => {
                            maxScore += a.score;
                        });
                    } else {
                        (qu.possibleAnswers || []).forEach((a) => {
                            maxScore = Math.max(a.score, maxScore);
                        });
                    }
                    scoreCount += maxScore;
                });
                question.totalPossibleScore = scoreCount;
                return question;
            }
            return null;
        } catch (err) {
            console.log(err);
            return null;
        }
    });
    const qResults = await Promise.all(qPromises);
    return qResults.filter(Boolean);
};

/**
 * Formats raw appointment data with physician names from care team.
 * @param {Array} data - Raw appointment array (e.g. from fetchPatientAppointments)
 * @param {Array} careTeam - Patient care team (array of staff with id, firstName, lastName)
 * @returns {Array} - Appointments with physicianName and type
 */
export const formatAppointments = (data, careTeam = []) => {
    if (!Array.isArray(data)) return [];
    return data
        .filter((appt) => !appt.deletedAt)
        .map((appt) => {
            const physician = (careTeam || []).filter((staff) => staff.id === appt.physicianId);
            const physicianName =
                physician.length > 0 ? `${physician[0].firstName} ${physician[0].lastName}` : "";
            return {
                ...appt,
                physicianName,
                type: "Appointment",
            };
        });
};
